import React from "react";
import { Card, CardBody, CardTitle, Button, Form, Input, FormFeedback, Spinner, FormGroup } from "reactstrap";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useResetPasswordMutation } from "../../../services/auth/api";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import ToastNotification from "components/Toast/ToastNotification";
import slash from "assets/icons/slash-circle.svg";

const ForgotPasswordReset = ({ userData, onSuccess }) => {
  const { email } = userData || {};
  const [resetPassword] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("กรุณากรอกรหัสผ่าน")
        .min(8, "รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร")
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,100}$/, "รหัสผ่านต้องประกอบด้วย ตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษอย่างน้อยอย่างละ 1 ตัว")
        .test("not-weak", "รหัสผ่านนี้ง่ายเกินไป กรุณาใช้รหัสผ่านที่รัดกุมกว่านี้", (value) => {
           const weakPasswords = ["password", "12345678", "123456789", "admin123", "password123", "qwerty", "11111111"];
           return !weakPasswords.includes(value ? value.toLowerCase() : "");
        }),
      confirm_password: Yup.string()
        .required("กรุณายืนยันรหัสผ่าน")
        .oneOf([Yup.ref("password"), null], "รหัสผ่านไม่ตรงกัน"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await resetPassword({ password: values.password });

        if (onSuccess) {
          onSuccess({ email, password: values.password });
        }
      } catch (error) {
        let id = toast.custom(
          <ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="เกิดข้อผิดพลาดในการตั้งรหัสผ่านใหม่" onSecondaryClick={() => toast.dismiss(id)} />,
          { position: "top-right", duration: 5000 }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Card className="card-auth">
      <CardTitle className="text-center">
        <h1 className="text-4xl fw-semibold">ตั้งรหัสผ่านใหม่</h1>
      </CardTitle>
      <CardBody>
        <Form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <h4 className="text-center text-gray-500 mb-4">กรุณาตั้งรหัสผ่านใหม่สำหรับบัญชีของคุณ</h4>

          <FormGroup className="space-y-4">
            <div className="form-horizontal w-full d-flex flex-column gap-4">
              <div>
                <div className="password-input-wrapper">
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="form-control form-control-lg"
                    placeholder="รหัสผ่าน"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.password}
                    disabled={validation.isSubmitting}
                    invalid={validation.touched.password && validation.errors.password ? true : false}
                  />
                  <button
                    className="password-toggle-btn"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={validation.isSubmitting}
                  >
                    <i className={showPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                  </button>
                </div>
                <p className="text-gray-500 pt-2 text-sm m-0">รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ</p>
                {validation.touched.password && validation.errors.password && <FormFeedback type="invalid" className="d-block">{validation.errors.password}</FormFeedback>}
              </div>

              <div>
                <div className="password-input-wrapper">
                  <Input
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control form-control-lg"
                    placeholder="ยืนยันรหัสผ่าน"
                    value={validation.values.confirm_password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    disabled={validation.isSubmitting}
                    invalid={validation.touched.confirm_password && validation.errors.confirm_password ? true : false}
                  />
                  <button
                    className="password-toggle-btn"
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={validation.isSubmitting}
                  >
                    <i className={showConfirmPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                  </button>
                </div>
                {validation.touched.confirm_password && validation.errors.confirm_password && <FormFeedback type="invalid" className="d-block">{validation.errors.confirm_password}</FormFeedback>}
              </div>
            </div>

            <div className="w-full">
              <Button type="submit" color="primary" size="lg" className="py-2 w-full" disabled={!validation.isValid || validation.isSubmitting}>
                {validation.isSubmitting ? (
                  <>
                    <Spinner size="sm" />
                    กำลังดำเนินการ...
                  </>
                ) : (
                  "บันทึกรหัสผ่านใหม่"
                )}
              </Button>
            </div>
          </FormGroup>
        </Form>
      </CardBody>
    </Card>
  );
};

ForgotPasswordReset.propTypes = {
  userData: PropTypes.shape({
    email: PropTypes.string.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ForgotPasswordReset;
